import { Edit, SimpleForm, TextInput, Toolbar, SaveButton, Confirm, useEditController, useDataProvider, useNotify, useRedirect } from 'react-admin';
import { Typography, Box, Divider, Tabs, Tab, Alert, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLayoutEffect, useRef, useState } from 'react';
import ContextTitle from '../../Components/ContextTitle';
import ConfigEditor, { ALLOWED_CONFIG_EDITORS, RawJsonConfigEditor, VENDORS_WITH_CONFIG_PANEL } from './ConfigEditor/ConfigEditor';
import { getIntegrationConfig } from '../../types/integrations';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export enum IntegrationStatus {
  STARTED = 'STARTED',
  PAUSED = 'PAUSED',
  DISABLED = 'DISABLED'
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`integration-tabpanel-${index}`} aria-labelledby={`integration-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const HcmEdit = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const controllerProps = useEditController();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const userEmail = JSON.parse(localStorage.getItem('GoogleAccountInfo') || '{}').email || '';
  const canEdit = ALLOWED_CONFIG_EDITORS.includes(userEmail);

  const hasConfigPanel = !!controllerProps.record && VENDORS_WITH_CONFIG_PANEL.includes(controllerProps.record.vendor);
  const configJsonTabIndex = hasConfigPanel ? 2 : 1;

  const prevHasConfigPanelRef = useRef(false);

  // When the record finishes loading, hasConfigPanel can flip from false to true and insert a tab at
  // index 1. Without adjusting activeTab, index 1 would switch from "Configuration" to "Config Panel".
  useLayoutEffect(() => {
    const prev = prevHasConfigPanelRef.current;
    if (!prev && hasConfigPanel) {
      setActiveTab((t) => (t >= 1 ? t + 1 : t));
    } else if (prev && !hasConfigPanel) {
      setActiveTab((t) => {
        if (t >= 2) return t - 1;
        if (t === 1) return 0;
        return t;
      });
    }
    prevHasConfigPanelRef.current = hasConfigPanel;
  }, [hasConfigPanel]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Validation function for required fields
  const validateRequired = (value: any) => {
    if (!value || value.toString().trim() === '') {
      return 'This field is required';
    }
    return undefined;
  };

  // Validation function for syncInterval - required and only digits allowed
  const validateSyncInterval = (value: any) => {
    // First check if required
    const requiredError = validateRequired(value);
    if (requiredError) {
      return requiredError;
    }
    // Then check if only digits
    if (!/^\d+$/.test(value.toString())) {
      return 'Sync interval must contain only digits';
    }
    return undefined;
  };

  const handleSubmit = (data: any) => {
    // Prevent default submission and show confirmation
    setFormData(data);
    setConfirmOpen(true);
    return Promise.resolve(); // Prevent form from actually submitting
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setUpdateError(null);

    if (formData && controllerProps.record) {
      try {
        const integrationId = controllerProps.record.id;
        const { data: current } = await dataProvider.getOne('integrations', { id: integrationId });

        // Backend expects a full document; server-owned fields must reflect the latest state
        // at save time (not the Edit view's possibly stale record from initial load).
        const payload: any = {
          vendor: current.vendor,
          source: current.source,
          companyUuid: current.companyUuid,
          status: current.status,
          vendorCredentials: current.vendorCredentials,
          integrationConfig: getIntegrationConfig(current),
          customState: current.customState,
          baseUrl: formData.baseUrl,
          syncInterval: formData.syncInterval,
          otCredentials: {
            ...(current.otCredentials || {}),
            clientId: formData.otCredentials.clientId,
            clientSecret: formData.otCredentials.clientSecret
          }
        };

        await dataProvider.update('integrations', {
          id: integrationId,
          data: payload,
          previousData: current
        });

        notify('Integration updated successfully', { type: 'success' });
        redirect('show', 'integrations', controllerProps.record.id);
      } catch (error: any) {
        const message = error?.body?.message || error?.message || 'An unexpected error occurred while updating the integration.';
        setUpdateError(message);
        notify('Error updating integration', { type: 'error' });
      }
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setFormData(null);
  };

  const CustomToolbar = ({ disabled = false }: { disabled?: boolean }) => (
    <Toolbar>
      <SaveButton disabled={disabled} />
    </Toolbar>
  );

  return (
    <>
      <Edit title={<ContextTitle context="uuid" />}>
        <Box sx={{ width: '100%' }}>
          <Collapse in={!!updateError}>
            <Alert
              severity="error"
              sx={{ m: 2, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
              action={
                <IconButton size="small" onClick={() => setUpdateError(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {updateError}
            </Alert>
          </Collapse>
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="integration settings tabs">
                <Tab label="General Info" />
                {hasConfigPanel && <Tab label="Config Panel" />}
                <Tab label="Configuration" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <SimpleForm onSubmit={handleSubmit} toolbar={<CustomToolbar disabled={!canEdit} />}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Integration Settings
                  </Typography>
                  <TextInput source="baseUrl" label="Base URL" fullWidth />
                  <TextInput source="syncInterval" label="Sync Interval (minutes)" validate={validateSyncInterval} fullWidth />
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    OT Credentials
                  </Typography>
                  <TextInput source="otCredentials.clientId" label="Client ID" validate={validateRequired} fullWidth />
                  <TextInput source="otCredentials.clientSecret" label="Client Secret" validate={validateRequired} fullWidth />
                </Box>
              </SimpleForm>
            </TabPanel>

            {hasConfigPanel && (
              <TabPanel value={activeTab} index={1}>
                <ConfigEditor integration={controllerProps.record!} />
              </TabPanel>
            )}

            <TabPanel value={activeTab} index={configJsonTabIndex}>
              {controllerProps.record ? (
                <RawJsonConfigEditor integration={controllerProps.record} canEdit={canEdit} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading integration…
                </Typography>
              )}
            </TabPanel>
          </>
        </Box>
      </Edit>
      <Confirm isOpen={confirmOpen} title="Confirm Save" content="Are you sure you want to save these changes to the integration?" onConfirm={handleConfirm} onClose={handleCancel} />
    </>
  );
};

export default HcmEdit;
